import React from "react";
import { motion } from "framer-motion";
import { PageHero, Overline, PrimaryButton } from "@/components/common";
import { Reveal } from "@/lib/motion";
import { SERVICES, SERVICES_OTHER, IMAGES } from "@/data/site";

export default function Services() {
  return (
    <>
      <PageHero
        overline="Our Services"
        title="A full range of building services"
        intro="We provide a full range of refurbishment and building services for industrial premises, commercial buildings and properties at end of lease."
        image={IMAGES.hayesInt1}
      />

      <section className="container-x py-20 md:py-28">
        <div className="grid gap-px border-l border-t border-slate-200 md:grid-cols-2">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.id}
              id={s.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-6%" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="group scroll-mt-28 border-b border-r border-slate-200 bg-white"
            >
              <div className="clip-frame aspect-[16/9] w-full overflow-hidden">
                <img src={s.image} alt={s.title} className="h-full w-full object-cover" loading="lazy" />
              </div>
              <div className="p-8 md:p-10">
                <span className="font-display text-sm text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="mt-2 font-display text-2xl font-medium uppercase tracking-tight text-[#0A1128] md:text-3xl">
                  {s.title}
                </h2>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600 md:text-base">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Other services — listed */}
      <section className="border-t border-slate-200 bg-[#F5F7FA] py-20 md:py-24">
        <div className="container-x">
          <Overline>Also part of what we do</Overline>
          <h2 className="mt-5 font-display text-3xl font-bold uppercase tracking-tight text-[#0A1128] md:text-4xl">
            Additional services
          </h2>
          <div className="mt-10 grid gap-px border-l border-t border-slate-200 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES_OTHER.map((s) => (
              <div
                key={s}
                data-testid={`service-other-${s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
                className="flex items-center gap-4 border-b border-r border-slate-200 bg-white p-6"
              >
                <span className="h-2 w-2 shrink-0 bg-[#1D4ED8]" />
                <span className="font-display text-lg uppercase tracking-tight text-[#0A1128]">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#1E293B] py-24 md:py-32">
        <div className="noise" />
        <div className="container-x relative">
          <Overline dark>Every project, to the highest standard</Overline>
          <Reveal>
            <p className="mt-8 max-w-4xl text-balance font-display text-2xl font-medium leading-tight tracking-tight text-white md:text-4xl">
              Whether you need a full building transformation, compliance upgrades, or essential repairs — we deliver
              every project with safety, quality, and minimal disruption at the core.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-12">
              <PrimaryButton to="/contact" testId="services-contact-btn" light>
                Request a Call Back
              </PrimaryButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
